import React from 'react';
import Modal from '../../Modal.jsx';
import SelectBox from '../reusable/SelectBox/SelectBox.jsx';
import JoditEditor from 'jodit-react';
import 'jodit/es5/jodit.min.css';
import PropTypes from 'prop-types';
import { makeEditorConfig } from "../reusable/WYSIWYGEditor/joditConfig.js";

export default class TemplateEditor extends React.Component {

  static propTypes = {
    templateTypeOptions: PropTypes.array.isRequired,
    templateData: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      template_text: PropTypes.string,
      template_type: PropTypes.string,
      language: PropTypes.string,
      updated_at: PropTypes.string,
      updated_by_user_id: PropTypes.number,
      updated_by_user_name: PropTypes.string,
      margin_vertical: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      margin_horizontal: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }),
    templateTypeMap: PropTypes.object.isRequired,
    languageMap: PropTypes.object.isRequired,
    languageOptions: PropTypes.array.isRequired,
    displayCreateTemplateFormModal: PropTypes.bool.isRequired,
    closeTemplateModalDOM: PropTypes.func,
    createUpdateTemplate: PropTypes.func.isRequired,
    type: PropTypes.oneOf(['create', 'edit', '']),
    readOnly: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      templateData: this.props.templateData || {
        id: 0,
        name: '',
        template_text: '',
        template_type: this.props.templateTypeMap.Gap,
        language: this.props.languageMap.English,
        updated_at: '',
        updated_by_user_id: '',
        updated_by_user_name: '',
        // Page margins (inches) saved with the template - TS-17667.
        margin_vertical: 1,
        margin_horizontal: 1
      }
    };
    // Print button in the template editor honors the template's own margins.
    this.editorConfig = {
      ...makeEditorConfig(() => ({
        marginVertical: parseFloat(this.state.templateData.margin_vertical) || 1,
        marginHorizontal: parseFloat(this.state.templateData.margin_horizontal) || 1
      })),
      readonly: this.props.readOnly || false
    };
    this.createUpdateTemplate = this.createUpdateTemplate.bind(this);
    this.updateState = this.updateState.bind(this);
    this.isCreateDisabled = this.isCreateDisabled.bind(this);
    this.getTitle = this.getTitle.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      templateData: nextProps.templateData
    });
  }

  createUpdateTemplate() {
    const templateObject = {
      "name": this.state.templateData.name.trim(),
      "template_text": btoa(unescape(encodeURIComponent(this.state.templateData.template_text))),
      "template_type": this.state.templateData.template_type,
      "language": this.state.templateData.language
    };
    // Margins are a GAP-only feature (TS-17667); other template types ignore
    // them at print/PDF time, so don't send them for non-GAP templates.
    if (this.state.templateData.template_type === this.props.templateTypeMap.Gap) {
      templateObject.margin_vertical = parseFloat(this.state.templateData.margin_vertical) || 1;
      templateObject.margin_horizontal = parseFloat(this.state.templateData.margin_horizontal) || 1;
    }
    this.props.createUpdateTemplate(templateObject);
  }

  updateState(state, value) {
    const templateData = this.state.templateData;
    templateData[state] = value;
    this.setState({ templateData });
  }

  isCreateDisabled() {
    return !(this.state.templateData.name && 
      this.state.templateData.template_text &&
      this.state.templateData.template_type &&
      this.state.templateData.language) ||
      this.props.readOnly;
  }

  getTitle() {
    if (this.props.type === 'edit') {
      return 'Update Template';
    } else {
      return "Create New Template";
    }
  }

  render() {
    return (
      <section>
        <Modal visible={this.props.displayCreateTemplateFormModal} close={this.props.closeTemplateModalDOM}
          size="large">
          <div className="row justify-content-center">
            <div className="col-11">
              <h3>{ this.getTitle() }</h3>
              <div className="container pt-3">
                <div className="row">
                  <div className="col-5">
                    <div className="form-group row justify-content-end">
                      <label className="col-form-label col-form-label-sm col-2 px-0">
                        Name:
                      </label>
                      <div className="col-10">
                        <input type="text"
                          className="form-control"
                          disabled={ this.props.type === 'edit' }
                          value={ this.state.templateData.name }
                          onChange={ (event)=>{this.updateState('name', event.target.value);} }/>
                      </div>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="form-group row justify-content-end">
                      <label className="col-form-label col-form-label-sm col-4 text-right">
                        Type:
                      </label>
                      <div className="col-8">
                        <SelectBox value={this.state.templateData.template_type}
                          disabled={ this.props.type === 'edit' }
                          onChange={(templateType)=>{this.updateState('template_type', templateType);}}
                          optionsList={this.props.templateTypeOptions}/>
                      </div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="form-group row justify-content-end">
                      <label className="col-form-label col-form-label-sm col-4 text-right">
                        Language:
                      </label>
                      <div className="col-8">
                        <SelectBox value={this.state.templateData.language}
                          disabled={ this.props.type === 'edit' }
                          onChange={(language)=>{this.updateState('language', language);}}
                          optionsList={this.props.languageOptions}/>
                      </div>
                    </div>
                  </div>
                </div>
                {this.state.templateData.template_type === this.props.templateTypeMap.Gap &&
                <div className="row">
                  <div className="col-4">
                    <div className="form-group row justify-content-end">
                      <label className="col-form-label col-form-label-sm col-6 text-right">
                        Top/Bottom margin (in):
                      </label>
                      <div className="col-6">
                        <input type="number" min="0.25" max="4" step="0.25"
                          className="form-control"
                          disabled={ this.props.readOnly }
                          value={ this.state.templateData.margin_vertical }
                          onChange={ (event)=>{this.updateState('margin_vertical', event.target.value);} }/>
                      </div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="form-group row justify-content-end">
                      <label className="col-form-label col-form-label-sm col-6 text-right">
                        Left/Right margin (in):
                      </label>
                      <div className="col-6">
                        <input type="number" min="0.25" max="4" step="0.25"
                          className="form-control"
                          disabled={ this.props.readOnly }
                          value={ this.state.templateData.margin_horizontal }
                          onChange={ (event)=>{this.updateState('margin_horizontal', event.target.value);} }/>
                      </div>
                    </div>
                  </div>
                </div>
                }
              </div>
              <div>
                <div>
                  <JoditEditor
                    value={this.state.templateData.template_text}
                    config={this.editorConfig}
                    onChange={(newContent)=>{this.updateState('template_text', newContent);}}/>
                </div>
              </div>
              <div className="row justify-content-end py-3 pr-3">
                <button className="btn btn-secondary mr-3 cursor-pointer" onClick={this.props.closeTemplateModalDOM}>Cancel
                </button>
                <button type="submit"
                  className="btn btn-primary cursor-pointer"
                  onClick={ this.createUpdateTemplate }
                  disabled={ this.isCreateDisabled() }>{ this.props.type === 'create' ? 'Create' : 'Update'}</button>
              </div>
            </div>
          </div>
        </Modal>
      </section>
    );
  }
}